import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import '../../scss/index.scss';

const BenchmarkScatterPlot = ({
    data = [],
    fullData = [],
    onAreaSelect = () => { },
    onPointClick = () => { },
    selectedMDRM,
    hiddenSchedules,
    setHiddenSchedules,
    filteredTableSchedules = new Set(),
    onResetSelection,
}) => {
    const svgRef = useRef();
    const tooltipRef = useRef();
    const [selectMode, setSelectMode] = useState(false);

    const points = data
        .filter((d) => typeof d.difference_percent === 'number')
        .map((d) => {
            const values = [...(d.compare_with || []), d.institution_a]
                .map((i) => parseFloat(i?.value))
                .filter((v) => !Number.isNaN(v));
            const avg = values.length
                ? values.reduce((a, b) => a + b, 0) / values.length
                : 0;

            return {
                mdrm: d.mdrm,
                itemName: d.item_name,
                schedule: d.schedule ? d.schedule.trim() : '',
                x: d.difference_percent,
                y: avg,
                color: d.color || '#ccc',
            };
        });

    const scheduleColorMap = {};
    points.forEach((p) => {
        if (p.schedule && p.color) {
            scheduleColorMap[p.schedule] = p.color;
        }
    });

    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const bounds = svgRef.current.getBoundingClientRect();
        const width = bounds.width || 800;
        const height = 600;

        const margin = {
            top: 40,
            right: 40,
            bottom: 120,
            left: 8,
        };
        const plotWidth = width - margin.left - margin.right;
        const plotHeight = height - margin.top - margin.bottom;

        const plot = svg
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${margin.left}, ${margin.top})`);

        //if (points.length === 0) return;

        const exponent = 0.3;
        const symmetricPow = (x) => Math.sign(x) * Math.abs(x) ** exponent;
        const inverseSymmetricPow = (y) => Math.sign(y) * Math.abs(y) ** (1 / exponent);

        const displayedPoints = points.filter((d) => !hiddenSchedules.has(d.schedule));

        const rawXExtent = d3.extent(points, (d) => d.x);
        const x = d3
            .scaleLinear()
            .domain([symmetricPow(rawXExtent[0]), symmetricPow(rawXExtent[1])])
            .range([0, plotWidth]);

        const rawXTicks = d3
            .range(-3, 4)
            .flatMap((p) => [-(10 ** p), 10 ** p])
            .concat(0);

        let lastTickX = -Infinity;
        const minSpacing = 40;
        const filteredTicks = rawXTicks
            .filter((v) => v >= rawXExtent[0] && v <= rawXExtent[1])
            .map(symmetricPow)
            .filter((d) => {
                const px = x(d);
                if (px - lastTickX >= minSpacing) {
                    lastTickX = px;
                    return true;
                }
                return false;
            });

        const xAxis = d3
            .axisBottom(x)
            .tickValues(filteredTicks)
            .tickFormat((d) => `${inverseSymmetricPow(d).toFixed(0)}%`);

        const yExtent = d3.extent(points, (d) => d.y);
        const y = d3
            .scaleLog()
            .domain([Math.max(1e-2, yExtent[0]), yExtent[1]])
            .range([plotHeight, 0])
            .nice();

        const yAxis = d3.axisLeft(y).ticks(6, '~s');

        plot.append('g')
            .attr('class', 'x-grid')
            .attr('transform', `translate(0,${plotHeight})`)
            .call(
                d3
                    .axisBottom(x)
                    .tickValues(filteredTicks)
                    .tickSize(-plotHeight)
                    .tickFormat(() => ''),
            )
            .selectAll('line')
            .attr('stroke', '#ccc')
            .attr('stroke-dasharray', '2,2');

        plot.append('g')
            .attr('class', 'y-grid')
            .call(
                d3
                    .axisLeft(y)
                    .ticks(6)
                    .tickSize(-plotWidth)
                    .tickFormat(() => ''),
            )
            .selectAll('line')
            .attr('stroke', '#ccc')
            .attr('stroke-dasharray', '2,2');

        plot.append('g').attr('transform', `translate(0,${plotHeight})`).call(xAxis);
        plot.append('g').call(yAxis);

        plot.append('text')
            .attr('x', plotWidth / 2)
            .attr('y', plotHeight + 50)
            .style('text-anchor', 'middle')
            .style('font-size', '12px')
            .text('Difference (%)');

        plot.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('x', -plotHeight / 2)
            .attr('y', -45)
            .style('text-anchor', 'middle')
            .style('font-size', '12px')
            .text('Average Value');

        const tooltip = d3.select(tooltipRef.current);

        plot.selectAll('circle')
            .data(displayedPoints)
            .enter()
            .append('circle')
            .attr('cx', (d) => x(symmetricPow(d.x)))
            .attr('cy', (d) => y(d.y))
            .attr('r', (d) => (selectedMDRM === d.mdrm ? 5 : 3))
            .attr('fill', (d) =>
                selectedMDRM === d.mdrm ? '#d93954' : d.color || '#ccc',
            )
            .style('cursor', 'pointer')
            .style('opacity', 0.8)
            .on('click', (_, d) => onPointClick?.(d.mdrm))
            .on('mouseover', (event, d) => {
                const svgRect = svgRef.current.getBoundingClientRect();
                tooltip
                    .style('opacity', 1)
                    .html(
                        `<strong>${d.mdrm}</strong><br/>Δ: ${d.x.toFixed(2)}%<br/>Value: ${d.y.toFixed(2)}<br/>${d.itemName}`,
                    )
                    .style('left', `${event.clientX - svgRect.left + 10}px`)
                    .style('top', `${event.clientY - svgRect.top - 40}px`);
            })
            .on('mousemove', (event) => {
                const svgRect = svgRef.current.getBoundingClientRect();
                tooltip
                    .style('left', `${event.clientX - svgRect.left + 10}px`)
                    .style('top', `${event.clientY - svgRect.top - 40}px`);
            })
            .on('mouseout', () => tooltip.style('opacity', 0));

        if (selectMode) {
            const brush = d3
                .brush()
                .extent([
                    [0, 0],
                    [plotWidth, plotHeight],
                ])
                .on('end', (event) => {
                    const { selection } = event;
                    if (selection) {
                        const [[x0, y0], [x1, y1]] = selection;
                        const area = {
                            x0: inverseSymmetricPow(x.invert(x0)),
                            x1: inverseSymmetricPow(x.invert(x1)),
                            y0: y.invert(y1),
                            y1: y.invert(y0),
                        };
                        onAreaSelect(area);
                        brushGroup.call(brush.move, null);
                    } else {
                        onAreaSelect(null);
                    }
                });

            const brushGroup = plot.append('g').attr('class', 'brush').call(brush);

            brushGroup.selectAll('.overlay').attr('cursor', 'crosshair');

            brushGroup
                .selectAll('.selection')
                .attr('stroke', '#999')
                .attr('stroke-width', 1)
                .attr('fill', '#999')
                .attr('fill-opacity', 0.3);
        }
    }, [data, selectMode, onAreaSelect, selectedMDRM, onPointClick, hiddenSchedules]);

    const allLegends = React.useMemo(() => {
        const map = new Map();
        fullData.forEach((d) => {
            const key = `${d.schedule}`;
            if (!map.has(key)) {
                map.set(key, {
                    schedule: d.schedule,
                    color: d.color || '#ccc',
                });
            }
        });
        return Array.from(map.values());
    }, [fullData]);

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <div className="area-selection-header">
                <label htmlFor="enable-select-mode" className="checkbox-label">
                    <input
                        id="enable-select-mode"
                        type="checkbox"
                        checked={selectMode}
                        onChange={() => setSelectMode((prev) => !prev)}
                    />
                    Enable Area Selection
                </label>

                <button
                    type="button"
                    className="clear-all"
                    onClick={onResetSelection}
                >
                    Reset Selection
                </button>
            </div>

            <svg ref={svgRef} style={{ width: '100%', height: '600px' }} />
            <div
                ref={tooltipRef}
                style={{
                    position: 'absolute',
                    opacity: 0,
                    background: '#333',
                    color: '#fff',
                    padding: '6px 8px',
                    fontSize: '12px',
                    borderRadius: '4px',
                    pointerEvents: 'none',
                    zIndex: 10,
                }}
            />

            {/* Legend */}
            <div className="bm-legend-wrapper">
                <strong className="bm-legend-title">Legend:</strong>
                <div className="bm-schedules-wrapper">
                    {allLegends.map(({ schedule, color }) => {
                        const isUserHidden = hiddenSchedules.has(schedule);
                        const isInFilteredTable = filteredTableSchedules.has(schedule);

                        const isVisuallyDisabled = isUserHidden || !isInFilteredTable;

                        return (
                            <div
                                key={schedule}
                                className="sp-schedules"
                                style={{
                                    gap: 0,
                                    opacity: isVisuallyDisabled ? 0.5 : 1,
                                    pointerEvents: 'auto',
                                    filter: isVisuallyDisabled ? 'grayscale(1)' : 'none',
                                }}
                                onClick={() => {
                                    setHiddenSchedules((prev) => {
                                        const copy = new Set(prev);
                                        if (copy.has(schedule)) copy.delete(schedule);
                                        else copy.add(schedule);
                                        return copy;
                                    });
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setHiddenSchedules((prev) => {
                                            const copy = new Set(prev);
                                            if (copy.has(schedule)) copy.delete(schedule);
                                            else copy.add(schedule);
                                            return copy;
                                        });
                                    }
                                }}
                            >
                                <div
                                    className="sp-schedule bm-schedule-wrapper"
                                    style={{
                                        backgroundColor: isUserHidden
                                            ? '#ccc'
                                            : color || '#888',
                                    }}
                                />
                                <span
                                    className="bm-schedule"
                                    style={{
                                        textDecoration: isVisuallyDisabled
                                            ? 'line-through'
                                            : 'none',
                                        color: isVisuallyDisabled ? '#999' : 'inherit',
                                    }}
                                >
                                    {schedule}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default BenchmarkScatterPlot;
